import { AxiosError } from 'axios';
import { AuthError as AmplifyAuthError } from 'aws-amplify/auth';

const NOT_ALLOWED_ERR_MESSAGES = {
  DELETE_ACCOUNT_ENTERPRISE_ERROR: 'This account is a paid enterprise account and cannot be deleted',
  DELETE_ACCOUNT_USERS_COUNT_ERROR: 'You must remove all other users before you can delete this account',
  DELETE_ACCOUNT_GROUP_LICENSE_ERROR: 'This account cannot be deleted. The account has DSOS licenses attributed to it',
  DELETE_ACCOUNT_DISTRIBUTOR_ERROR: 'This account cannot be deleted. It is owed by a distributor',

  FEATURE_SETS_ASSIGN_INVALID_PLAYER: 'is not a valid device',
  FEATURE_SETS_ASSIGN_EXISTS: 'already has feature set',
  FEATURE_SETS_ASSIGN_LICENSE_EXISTS: 'already has a license assigned. Cannot assign feature set.',
  FEATURE_SETS_ASSIGN_NO_SUBSCRIPTION: 'does not have feature sets configured',
  FEATURE_SETS_ASSIGN_NO_CAPACITY_CONFIGURED: 'does not have capacity configured for feature set',
  FEATURE_SETS_ASSIGN_EXPIRED: /Feature sets for group (.*?) have expired on/,
  FEATURE_SETS_ASSIGN_NO_CAPACITY: 'does not have capacity for feature set'
};

type ApiError = {
  description?: string;
  exception?: string;
  message?: string;
  name?: string;
  statusCode?: number;
  requestId?: string;
};

export class AppError extends Error {
  statusCode?: number | undefined; // Numeric error code
  translatedMessage?: string | undefined; // Translated error message (using i18n)
  requestId?: string; // Amazon Request ID to trace request

  /**
   * Constructor for the AppError class.
   * @param name - The name of the error.
   * @param message - Default error message.
   * @param statusCode - Numeric code sent by server.
   * @param translatedMessage - i18n translation error message key.
   * @param cause - The original error that caused this one.
   */
  constructor(
    name: string,
    message: string,
    translatedMessage?: string,
    statusCode?: number,
    cause?: AxiosError | AmplifyAuthError | Error,
    requestId?: string
  ) {
    super(message);
    this.statusCode = statusCode;
    this.name = name;
    this.message = message;
    this.translatedMessage = translatedMessage;
    this.cause = cause;
    this.requestId = requestId;

    Object.setPrototypeOf(this, AppError.prototype);
  }

  override toString(): string {
    const message = [`AppError [${this.name}] - ${this.message}`];

    if (this.statusCode) {
      message.push('statusCode: ' + this.statusCode);
    }

    if (this.translatedMessage) {
      message.push('translatedMessage: ' + this.translatedMessage);
    }

    if (this.cause) {
      message.push('cause: ' + this.cause);
    }

    return message.join('\n');
  }
}

export const ErrorHandler = {
  processError(error: AxiosError | AmplifyAuthError | Error) {
    if (error instanceof AxiosError) {
      const message = this.parseApiError(error);
      const requestId = error.response?.headers
        ? typeof error.response.headers.get === 'function'
          ? error.response.headers.get('x-amzn-requestid')
          : error.response.headers['x-amzn-requestid'] ?? null
        : null;

      return new AppError(
        message.name,
        message.message,
        message.translatedMessage,
        message.code,
        error,
        requestId
      );
    } else if (error instanceof AmplifyAuthError) {
      const message = this.parseCognitoError(error);

      return new AppError(
        message.name,
        message.message,
        message.translatedMessage,
        undefined,
        error
      );
    } else if (error instanceof AppError) {
      return error;
    }

    // Fallback for unknown error
    return new AppError(
      'UnknownError',
      'An unexpected error occurred.',
      'errors.unexpectedError',
      undefined,
      error
    );
  },

  parseApiError(error: AxiosError) {
    if (error?.response?.status) {
      const responseData: ApiError = error.response.data as ApiError;

      switch (error.response.status) {
        case 400: {
          const name = responseData?.name ?? responseData?.exception ?? 'BadClientRequest';
          let translatedMessage = 'errors.400';

          if (name === 'AliasExistsException') {
            translatedMessage = 'errors.existing.accountEmail';
          }

          return {
            code: 400,
            name: name,
            message: responseData?.message ?? responseData?.description ?? 'Something went wrong. Please check your input and try again.',
            translatedMessage
          };
        } case 401:
          return {
            code: 401,
            name: responseData?.name ?? responseData?.exception ?? 'UnauthorizedError',
            message: responseData?.message ?? responseData?.description ?? 'Unauthorized. Please log in again.',
            translatedMessage: 'errors.401'
          };
        case 403: {
          const name = responseData?.name ?? responseData?.exception ?? 'ForbiddenError';
          const message = responseData?.message ?? responseData?.description ?? 'Forbidden. You do not have access.';
          let translatedMessage = 'errors.403';

          if (name === 'PinError') {
            if (message.includes('Pin blocked, restart player to unblock')) {
              translatedMessage = 'errors.invalid.playerPinBlocked';
            } else {
              translatedMessage = 'errors.invalid.playerPin';
            }
          } else if (name === 'NotAllowedError') {
            // Account deletion errors
            if (message.includes(NOT_ALLOWED_ERR_MESSAGES.DELETE_ACCOUNT_ENTERPRISE_ERROR)) {
              translatedMessage = 'errors.deleteAccountEnterpriseError';
            } else if (message.includes(NOT_ALLOWED_ERR_MESSAGES.DELETE_ACCOUNT_USERS_COUNT_ERROR)) {
              translatedMessage = 'account.deleteAccountUsersCountError';
            } else if (message.includes(NOT_ALLOWED_ERR_MESSAGES.DELETE_ACCOUNT_GROUP_LICENSE_ERROR)) {
              translatedMessage = 'errors.accountDeleteFeatureSets';
            } else if (message.includes(NOT_ALLOWED_ERR_MESSAGES.DELETE_ACCOUNT_DISTRIBUTOR_ERROR)) {
              translatedMessage = 'errors.accountDeleteFeatureSets';
            } else if (message.includes(NOT_ALLOWED_ERR_MESSAGES.FEATURE_SETS_ASSIGN_INVALID_PLAYER)) {
              translatedMessage = 'players.playerNotValidDevice';
            } else if (message.includes(NOT_ALLOWED_ERR_MESSAGES.FEATURE_SETS_ASSIGN_EXISTS)) {
              translatedMessage = 'players.playerAlreadyHasFeatureSet';
            } else if (message.includes(NOT_ALLOWED_ERR_MESSAGES.FEATURE_SETS_ASSIGN_LICENSE_EXISTS)) {
              translatedMessage = 'players.playerAlreadyHasLicense';
            } else if (message.includes(NOT_ALLOWED_ERR_MESSAGES.FEATURE_SETS_ASSIGN_NO_SUBSCRIPTION)) {
              translatedMessage = 'players.accountFeatureSetsNotConfigured';
            } else if (message.includes(NOT_ALLOWED_ERR_MESSAGES.FEATURE_SETS_ASSIGN_NO_CAPACITY_CONFIGURED)) {
              translatedMessage = 'players.accountFeatureSetCapacityNotConfigured';
            } else if (NOT_ALLOWED_ERR_MESSAGES.FEATURE_SETS_ASSIGN_EXPIRED.test(message)) {
              translatedMessage = 'players.accountFeatureSetsExpired';
            } else if (message.includes(NOT_ALLOWED_ERR_MESSAGES.FEATURE_SETS_ASSIGN_NO_CAPACITY)) {
              translatedMessage = 'players.accountFeatureSetsNoCapacity';
            }
          } else if (name === 'ExceededAllowableUsersError') {
            translatedMessage = 'errors.exceedMaximumUsersError';
          }

          return {
            code: 403,
            name: name,
            message: message,
            translatedMessage: translatedMessage
          };
        }
        case 404: {
          const message = responseData?.message ?? responseData?.description ?? 'Resource not found.';
          let translatedMessage = 'errors.404';

          if (/player.*not found/i.test(message)) {
            translatedMessage = 'errors.invalid.playerSerialNumber';
          } else if (message.includes('Provider Not Found')) {
            translatedMessage = 'errors.invalid.provider';
          }

          return {
            code: 404,
            name: responseData?.name ?? responseData?.exception ?? 'NotFoundError',
            message: message,
            translatedMessage: translatedMessage
          };
        }
        case 409: {
          const name = responseData?.name ?? responseData?.exception ?? 'ConflictError';
          const message = responseData?.message ?? responseData?.description ?? 'Conflict. Please try again.';
          let translatedMessage = 'errors.409';

          if (name === 'OnlyAccountError') {
            translatedMessage = 'errors.leaveAccountOnlyAccountError';
          } else if (name === 'EmailExistsError') {
            translatedMessage = 'errors.existing.accountEmail';
          }

          return {
            code: 409,
            name: name,
            message: message,
            translatedMessage: translatedMessage
          };
        }
        case 412: {
          const name = responseData?.name ?? responseData?.exception ?? 'PreconditionError';
          const message = responseData?.message ?? responseData?.description ?? 'Precondition Failed.';
          let translatedMessage = 'errors.412';

          if (name === 'ExceededMaximumPlayersError' || message.includes('Player limit reached')) {
            translatedMessage = 'errors.exceededMaximumPlayersError';
          } else if (name === 'UserExistsError') {
            translatedMessage = 'errors.existing.userInAccount';
          } else if (name === 'OnlyUserInAccountError') {
            translatedMessage = 'errors.onlyUserInAccount';
          } else if (name === 'OnlyAdminInAccountError') {
            translatedMessage = 'errors.onlyGlobalAdminInAccount';
          } else if (name === 'InvalidEmailError') {
            translatedMessage = 'errors.invalid.emailFormat';
          }

          return {
            code: 412,
            name: name,
            message: message,
            translatedMessage: translatedMessage
          };
        }
        case 500:
          return {
            code: 500,
            name: responseData?.name ?? responseData?.exception ?? 'InternalServerError',
            message: responseData?.message ?? responseData?.description ?? 'Server error. Please try again later.',
            translatedMessage: 'errors.500'
          };
      }
    } else if (error.code === 'ERR_NETWORK') {
      return {
        code: 500,
        name: 'NetworkError',
        message: 'Server error. Please try again later.',
        translatedMessage: 'errors.500'
      };
    }

    return {
      name: 'GenericError',
      message: 'Unexpected error. Please try again.',
      translatedMessage: 'errors.unexpectedError'
    };
  },

  parseCognitoError(error: AmplifyAuthError) {
    // Parse common Cognito errors
    const cognitoErrorMessages = {
      UserNotFoundException: 'errors.invalid.userOrPassword', // 'User does not exist.',
      NotAuthorizedException: 'errors.invalid.userOrPassword', // 'Incorrect username or password.',
      UsernameExistsException: 'errors.existing.accountEmail', // 'User already exists.',
      CodeMismatchException: 'errors.invalid.verificationCode', // Invalid verification code.
      ExpiredCodeException: 'errors.invalid.verificationCode', // 'The verification code has expired.'
      UserAlreadyAuthenticatedException: 'errors.existing.loggedInUser' // User already logged in
    };

    let errorMessage = null;

    switch (error.name) {
      case 'UserLambdaValidationException':
        if (error.message && error.message.includes('e-mail already exists')) {
          errorMessage = 'errors.existing.accountEmail';
        } else if (error.message && error.message.includes('Not allowed to authenticate via non-SSO Provider')) {
          errorMessage = 'errors.nonSSOAuthDenied';
        } else {
          errorMessage = error.message;
        }
        break;
      default:
        errorMessage = cognitoErrorMessages[error.name as keyof typeof cognitoErrorMessages];
        break;
    }

    return {
      name: error.name,
      message: error.message ?? error.name ?? 'AmplifyAuthError',
      translatedMessage: errorMessage ?? 'Authentication error occurred.'
    };
  }
};
